import { type CapabilityBucket, CapabilitySectionKind } from '../../../../api/agents-client/agent-client.type';
import {
  type BuildConnectionListInput,
  FloraConnectionId,
  type FloraConnectionListItem,
  type IntegrationStatusInput,
} from './agent-settings.type';

/**
 * Counts the tools in a catalog bucket.
 *
 * @param {CapabilityBucket} bucket - The connection bucket
 * @returns {number} Read + write tools across its capabilities
 */
function countBucketTools(bucket: CapabilityBucket): number {
  return bucket.capabilities.reduce((total, capability) => total + capability.read.length + capability.write.length, 0);
}

/**
 * Builds a "Docs, Sheets, Slides and Drive" phrase from a bucket's capabilities.
 *
 * @param {CapabilityBucket} bucket - The connection bucket
 * @returns {string} The capability names joined for display
 */
function describeBucket(bucket: CapabilityBucket): string {
  const names = bucket.capabilities.map((capability) => capability.name);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Describes a Slack/Teams style connection that has a workspace install plus
 * a per-user account link.
 *
 * @param {IntegrationStatusInput} status - The integration's status
 * @param {string} notConnectedCopy - What the integration does, shown until it is set up
 * @returns {string} The row description
 */
function describeWorkspaceIntegration(status: IntegrationStatusInput, notConnectedCopy: string): string {
  if (!status.installed) return notConnectedCopy;
  return status.userConnected
    ? 'Workspace installed · your account connected'
    : 'Workspace installed · connect your account';
}

/**
 * Merges the capability catalog's connection buckets (Google, Microsoft 365)
 * with the Google, Slack, and Teams status endpoints into the rows of the
 * Connections list, so each app appears once.
 *
 * Responsibilities:
 * 1. Add Google from its status endpoint, enriched with its catalog bucket's tools
 * 2. Add every other catalog bucket from the catalog's own connection state
 * 3. Add Slack and Teams when their feature flags are on
 *
 * @param {BuildConnectionListInput} input - The catalog and each integration's status
 * @returns {FloraConnectionListItem[]} The rows in display order
 */
function buildConnectionList({
  capabilities,
  google,
  slack,
  teams,
}: BuildConnectionListInput): FloraConnectionListItem[] {
  const buckets = (capabilities?.sections ?? []).filter(
    (section): section is CapabilityBucket => section.kind === CapabilitySectionKind.Bucket,
  );
  const googleBucket = buckets.find((bucket) => bucket.id === FloraConnectionId.Google);
  const googleConnected = google?.connected ?? googleBucket?.connected ?? false;
  const googleAccount = google?.email ?? googleBucket?.account ?? null;

  const items: FloraConnectionListItem[] = [
    {
      id: FloraConnectionId.Google,
      name: googleBucket?.name ?? 'Google',
      description:
        googleConnected && googleAccount
          ? `Signed in as ${googleAccount}`
          : googleBucket
            ? describeBucket(googleBucket)
            : 'Let Flora create and update files in your Google Drive.',
      connected: googleConnected,
      toolCount: googleBucket ? countBucketTools(googleBucket) : null,
    },
  ];

  for (const bucket of buckets) {
    if (bucket.id === FloraConnectionId.Google) continue;
    items.push({
      id: bucket.id,
      name: bucket.name,
      description: bucket.connected && bucket.account ? `Signed in as ${bucket.account}` : describeBucket(bucket),
      connected: bucket.connected,
      toolCount: countBucketTools(bucket),
    });
  }

  if (slack.enabled) {
    items.push({
      id: FloraConnectionId.Slack,
      name: 'Slack',
      description: describeWorkspaceIntegration(slack, 'Chat with Flora and get automation results in Slack.'),
      connected: slack.installed && slack.userConnected,
      toolCount: null,
    });
  }

  if (teams.enabled) {
    items.push({
      id: FloraConnectionId.Teams,
      name: 'Microsoft Teams',
      description: describeWorkspaceIntegration(teams, 'Chat with Flora and get automation results in Teams.'),
      connected: teams.installed && teams.userConnected,
      toolCount: null,
    });
  }

  return items;
}

export { buildConnectionList };
export type { BuildConnectionListInput, FloraConnectionListItem, IntegrationStatusInput };
